/**
 * API Service - שירות API
 * Axios configuration with authentication and error handling
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../store/authStore'

// Create axios instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // For cookies
})

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors and token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Handle 401 Unauthorized - Token expired
    // Skip refresh for auth endpoints (login, register, etc.) — 401 there means bad credentials, not expired token
    const isAuthEndpoint = originalRequest.url?.includes('/auth/')
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      try {
        // Try to refresh token
        const response = await axios.post(
          '/api/v1/auth/refresh',
          {},
          {
            withCredentials: true,
          }
        )

        const accessToken = response.data?.tokens?.accessToken

        // Update token in store
        useAuthStore.getState().setToken(accessToken)

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }

        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed - logout user
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    return Promise.reject(error)
  }
)

// API Error Handler
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      message?: string
      error?: string
      errors?: Array<{ field: string; message: string }>
    }>

    // Validation errors — check first (more specific than generic message)
    if (axiosError.response?.data?.errors?.length) {
      return axiosError.response.data.errors
        .map((err) => err.message)
        .join(', ')
    }

    // Server error message
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message
    }

    // Generic error message
    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error
    }

    // Network error
    if (axiosError.message === 'Network Error') {
      return 'שגיאת רשת - נסה שוב מאוחר יותר'
    }

    // Timeout error
    if (axiosError.code === 'ECONNABORTED') {
      return 'הבקשה לקחה יותר מדי זמן - נסה שוב'
    }

    // Default error
    return axiosError.message || 'שגיאה לא צפויה'
  }

  // Non-axios error
  return 'שגיאה לא צפויה'
}

export default api
